import type { SiteStoreWithInit } from '~/stores/site'
import { useSiteStore } from '~/stores/site'
import { getLocaleFromRouteName, resolveSiteLocale } from '~/utils/locale-util'
import { useNuxtApp } from '#app'

export default defineNuxtRouteMiddleware(async (to, from) => {
  const siteStore: SiteStoreWithInit = useSiteStore()
  if (!siteStore.initialization) {
    siteStore.initialization = {}
  }

  const config = useRuntimeConfig()
  const { $sanitizeText } = useNuxtApp()

  // The locale comes from the route i18n resolved (`routepath___fr`), never from a
  // `locale` route param — `prefix_except_default` does not create one, so reading
  // `to.params.locale` always fell through to 'en'. app.vue papered over that once at
  // boot, but this middleware runs on every navigation while app.vue's setup does not,
  // so any later run (a URL carrying a query string, a client-side route change) left
  // `siteStore.language` on 'en' while `useI18n().locale` stayed 'fr' — an English
  // header with a duplicated Home link on top of French page content.
  const routeLocale = getLocaleFromRouteName(to.name)

  const langQuery = Array.isArray(to.query.lang) ? to.query.lang[0] : to.query.lang
  const queryLang = langQuery ? $sanitizeText(langQuery) : null

  let collection = ''
  if (config.kontent) {
    collection = config.kontent.collection
  }
  if (to.query.collection && !config.public.isProduction) {
    const collectionQuery =
      Array.isArray(to.query.collection) ? to.query.collection[0] : to.query.collection
    if (collectionQuery) collection = $sanitizeText(collectionQuery)
  }

  siteStore.setCollection(collection)

  if (siteStore.initialization.fetchKontentData === undefined) {
    siteStore.initialization.fetchKontentData = siteStore.fetchData(
      false,
      routeLocale || siteStore.defaultLanguage
    )
  }

  try {
    await siteStore.initialization.fetchKontentData
  } catch (error) {
    console.error('Error fetching data:', error)
  }

  // Validated against the languages the site actually has a configuration for, so an
  // unsupported locale falls back to the default instead of clearing `siteConfig`.
  siteStore.switchLanguage(
    resolveSiteLocale({
      routeName: to.name,
      path: to.path,
      queryLang,
      availableLocales: siteStore.languages.map((language) => language.code),
      defaultLocale: siteStore.defaultLanguage
    })
  )

  // If no site configuration resolved for the active collection (an unknown
  // ?collection=, or a failed/empty config fetch), there is no site to render.
  // Surface a clean 404 here rather than letting downstream render code (layout
  // header/footer, useCustomCss, redirect middleware, …) dereference `.elements`
  // on an undefined config and throw an unhandled 500.
  if (!siteStore.siteConfig?.elements) {
    throw createError({ statusCode: 404, statusMessage: 'Site configuration not found' })
  }
})
